/**
 * UIComponents Module
 * 
 * Handles UI interactions, spinners, and user feedback including:
 * - Loading spinners and states
 * - Bid provider UI management
 * - Search and filtering UI
 * - Provider card generation
 */

import Logger from '../../lib/logger';

export class UIComponents {
  constructor(controller) {
    this.controller = controller;
  }

  // Helper to get a Stimulus target or fallback to getElementById
  getTarget(name, fallbackId) {
    const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
    const hasMethod = `has${capitalizedName}Target`;
    const targetMethod = `${name}Target`;

    if (this.controller[hasMethod]) {
      return this.controller[targetMethod];
    }
    return document.getElementById(fallbackId);
  }

  // Unified map URL builder
  buildMapUrl(baseUrl, extraParams = {}) {
    if (!this.controller.map || !this.controller.map.getBounds) return null;

    const bounds = this.controller.map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const boundsString = `${sw.lat},${sw.lng},${ne.lat},${ne.lng}`;
    
    // Ensure absolute URL if it doesn't start with / or http
    const url = new URL(baseUrl, window.location.origin);
    const endpoints = this.controller.getApiEndpoints();
    
    // Logic for bid_providers endpoint
    if (baseUrl === endpoints.BID_PROVIDERS) {
      url.searchParams.set('l', boundsString);
      if (this.controller.showRecentBidsOnlyValue) {
        url.searchParams.set('show_all', 'false');
      } else {
        url.searchParams.set('show_all', 'true');
      }
      
      const currentFilter = this.controller.getCurrentProviderFilter();
      if (currentFilter) {
        url.searchParams.set('provider_id', currentFilter.id);
      }
    }
    
    // Logic for bids_crm endpoint
    if (baseUrl === endpoints.BIDS_CRM) {
      url.searchParams.set('map_bounds', boundsString);
      url.searchParams.set('compact', 'true');
      url.searchParams.set('infinite', 'true');
      
      if (this.controller.sortByDistanceValue) {
        url.searchParams.set('sort_by_distance', 'true');
      }
      
      if (this.controller.showRecentBidsOnlyValue) {
        url.searchParams.set('recent', '1');
      }
      
      const currentFilter = this.controller.getCurrentProviderFilter();
      if (currentFilter) {
        url.searchParams.set('q[provider_id_eq]', currentFilter.id);
      }
    }

    // Add any manual extra params
    Object.keys(extraParams).forEach(key => url.searchParams.set(key, extraParams[key]));

    return url.pathname + url.search;
  }

  // Generic count badge updater
  updateCountBadge(targetName, fallbackId, count) {
    try {
      const badge = this.getTarget(targetName, fallbackId);
      if (badge) {
        badge.textContent = count;
        if (count > 0) {
          badge.classList.remove('d-none');
          badge.className = `badge bg-primary ms-1`;
        } else {
          badge.classList.add('d-none');
        }
      }
    } catch (error) {
      Logger.error(`Failed to update count for ${targetName}`, error);
    }
  }

  /**
   * Update badges immediately from server-provided metadata
   * @param {Object} meta - Metadata from map_data response
   */
  updateBadgesFromMeta(meta) {
    if (!meta) return;
    
    // total_addresses from meta represents sites/addresses
    if (typeof meta.total_addresses !== 'undefined') {
      this.updateCountBadge('providersCountBadge', 'bid-count', meta.total_providers || 0);
    }
    
    // total_bids from meta represents the large historical count
    if (typeof meta.total_bids !== 'undefined') {
      this.updateCountBadge('bidListCountBadge', 'bid-list-count', meta.total_bids);
    }
  }

  // Standardized loading state
  showComponentLoading(targetName, fallbackId, message = 'Loading...') {
    const container = this.getTarget(targetName, fallbackId);
    if (container) {
      container.innerHTML = `
        <div class="text-center text-muted py-4">
          <div class="spinner-border text-primary mb-3" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mb-0">${message}</p>
        </div>
      `;
    }
  }

  // Standardized error state
  showComponentError(targetName, fallbackId, message, retryAction) {
    const container = this.getTarget(targetName, fallbackId);
    if (container) {
      container.innerHTML = `
        <div class="text-center text-danger py-4">
          <i class="fas fa-exclamation-triangle fa-2x mb-3 text-opacity-50"></i>
          <p class="mb-2">${message}</p>
          <div class="mt-3">
            <button class="btn btn-sm btn-outline-danger" 
                    data-action="click->map-leaflet#${retryAction}">
              <i class="fas fa-redo me-1"></i>Try Again
            </button>
          </div>
        </div>
      `;
    }
  }

  // Show spinner with delay
  showSpinner(options = {}) {
    try {
      const delay = typeof options === 'number' ? options : (options.delay || 250);
      const isSilent = options.silent || false;

      if (this.controller.spinnerVisible || this.controller.spinnerTimeout) return;

      // If silent, only show the global map spinner, not component-specific ones
      if (!isSilent) {
        if (this.controller.isBidProvidersTabActive()) {
          this.showComponentLoading('providersContent', 'bid-providers-content', 'Loading providers...');
          return;
        }

        if (this.controller.isBidListTabActive()) {
          this.showComponentLoading('bidListContent', 'bid-list-content', 'Loading bids...');
          return;
        }
      }

      const spinner = this.getTarget('spinner', null);
      if (!spinner) return;

      this.controller.spinnerTimeout = setTimeout(() => {
        spinner.classList.remove('d-none', 'spinner-hidden');
        this.controller.spinnerVisible = true;
      }, delay);
    } catch (error) {
      Logger.error('Failed to show spinner', error);
    }
  }

  // Hide spinner
  hideSpinner() {
    try {
      if (this.controller.spinnerTimeout) {
        clearTimeout(this.controller.spinnerTimeout);
        this.controller.spinnerTimeout = null;
      }
      
      const spinner = this.getTarget('spinner', null);
      if (spinner) {
        spinner.classList.add('d-none', 'spinner-hidden');
      }
      this.controller.spinnerVisible = false;
    } catch (error) {
      Logger.error('Failed to hide spinner', error);
    }
  }

  // Legacy method aliases
  showBidProvidersLoading() {
    this.showComponentLoading('providersContent', 'bid-providers-content', 'Loading providers...');
  }

  updateBidProvidersContent(html) {
    const contentDiv = this.getTarget('providersContent', 'bid-providers-content');
    if (contentDiv) contentDiv.innerHTML = html;
  }

  async loadBidProviders(event, options = {}) {
    try {
      if (event) { event.preventDefault(); event.stopPropagation(); }
      this.hideDistanceSortContainer();

      if (!this.controller.map?.getBounds) {
        if (!options.silent) {
          this.showComponentError('providersContent', 'bid-providers-content', 'Map not initialized. Please refresh.', 'loadBidProviders');
        }
        return;
      }

      const endpoints = this.controller.getApiEndpoints();
      const url = this.buildMapUrl(endpoints.BID_PROVIDERS);
      
      if (!options.silent) {
        this.showComponentLoading('providersContent', 'bid-providers-content', 'Loading providers...');
      }
      
      const frame = this.getTarget('providersContent', 'bid-providers-content');
      if (frame?.tagName === 'TURBO-FRAME') {
        frame.src = url;
        frame.addEventListener('turbo:frame-load', () => {
          const countData = frame.querySelector('[data-count]');
          this.updateCountBadge('providersCountBadge', 'bid-count', parseInt(countData?.dataset.count || '0'));
        }, { once: true });
      }
    } catch (error) {
      Logger.error('Failed to load bid providers', error);
      if (!options.silent) {
        this.showComponentError('providersContent', 'bid-providers-content', 'Failed to load providers', 'loadBidProviders');
      }
      this.updateCountBadge('providersCountBadge', 'bid-count', 0);
    }
  }

  autoUpdateBidProvidersTab() {
    try {
      setTimeout(() => {
        const pane = this.getTarget('bidProvidersPane', 'bid-providers');
        if (pane?.classList.contains('active')) this.loadBidProviders();
      }, 500);
    } catch (error) {
      Logger.error('Failed in autoUpdateBidProvidersTab', error);
    }
  }

  /**
   * Load compact bid list for the current map area and display in tab
   * Fetches bid data based on current map bounds and updates the UI
   *
   * @param {Event} event - Optional click event from UI
   */
  async loadBidList(event, options = {}) {
    try {
      if (event) { event.preventDefault(); event.stopPropagation(); }
      this.showDistanceSortContainer();

      if (!this.controller.map?.getBounds) {
        if (!options.silent) {
          this.showComponentError('bidListContent', 'bidscrm', 'Map not initialized. Please refresh.', 'loadBidList');
        }
        return;
      }

      await this.fetchAndDisplayBidList(options);
    } catch (error) {
      Logger.error('Failed to load bid list', error);
      if (!options.silent) {
        this.showComponentError('bidListContent', 'bidscrm', 'Failed to load bid list', 'loadBidList');
      }
      this.updateCountBadge('bidListCountBadge', 'bid-list-count', 0);
    }
  }

  async fetchAndDisplayBidList(options = {}) {
    try {
      const endpoints = this.controller.getApiEndpoints();
      const url = this.buildMapUrl(endpoints.BIDS_CRM);
      
      if (!options.silent) {
        this.showComponentLoading('bidListContent', 'bidscrm', 'Loading bids...');
      }
      
      const frame = this.getTarget('bidListContent', 'bidscrm');
      if (frame?.tagName === 'TURBO-FRAME') {
        frame.src = url;
        frame.addEventListener('turbo:frame-load', () => {
          const countData = frame.querySelector('[data-count]');
          this.updateCountBadge('bidListCountBadge', 'bid-list-count', parseInt(countData?.dataset.count || '0'));
        }, { once: true });
      } else {
        const response = await fetch(url, { headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const html = await response.text();
        this.updateBidListContent(html);
        this.updateCountBadge('bidListCountBadge', 'bid-list-count', parseInt(response.headers.get('X-Total-Count') || '0'));
      }
    } catch (error) {
      Logger.error('Failed to fetch bid list', error);
      throw error;
    }
  }

  // Update bid list content in the tab
  updateBidListContent(html) {
    const contentDiv = this.getTarget('bidListContent', 'bidscrm');
    if (contentDiv) contentDiv.innerHTML = html;
  }

  // Append bid list content for infinite scroll
  appendBidListContent(html) {
    try {
      const contentDiv = this.controller.hasBidListContentTarget
        ? this.controller.bidListContentTarget
        : document.getElementById('bidscrm');
      if (contentDiv) {
        const tableBody = contentDiv.querySelector('tbody');
        if (tableBody) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newRows = doc.querySelectorAll('tbody tr');

          newRows.forEach(row => {
            tableBody.appendChild(row);
          });

          Logger.debug(`Appended ${newRows.length} new bid rows`);
        }
      }
    } catch (error) {
      Logger.error('Failed to append bid list content', error);
    }
  }

  /**
   * Handle infinite scroll loaded event - update badge count
   * @param {CustomEvent} event - The loaded event from infinite scroll controller
   */
  handleInfiniteScrollLoaded(event) {
    try {
      const { totalCount } = event.detail;
      if (typeof totalCount !== 'undefined') {
        const activeTab = this.controller.isBidProvidersTabActive() ? 'providers' : 'bidList';
        const badgeTarget = activeTab === 'providers' ? 'providersCountBadge' : 'bidListCountBadge';
        const fallbackId = activeTab === 'providers' ? 'bid-count' : 'bid-list-count';
        this.updateCountBadge(badgeTarget, fallbackId, totalCount);
      }
    } catch (error) {
      Logger.error('Failed to handle infinite scroll loaded', error);
    }
  }



  /**
   * Handle infinite scroll reset event
   * @param {CustomEvent} event - The reset event from infinite scroll controller
   */
  handleInfiniteScrollReset(event) {
    const activeTab = this.controller.isBidProvidersTabActive() ? 'providers' : 'bidList';
    const badgeTarget = activeTab === 'providers' ? 'providersCountBadge' : 'bidListCountBadge';
    const fallbackId = activeTab === 'providers' ? 'bid-count' : 'bid-list-count';
    this.updateCountBadge(badgeTarget, fallbackId, 0);
  }

  // Auto-update bid list tab if it's currently active
  autoUpdateBidListTab() {
    try {
      setTimeout(() => {
        const pane = this.getTarget('bidListPane', 'bids_t');
        if (pane?.classList.contains('active')) this.loadBidList();
      }, 500);
    } catch (error) {
      Logger.error('Failed in autoUpdateBidListTab', error);
    }
  }

  /**
   * Show the distance sort container in the tab header
   */
  showDistanceSortContainer() {
    const container = this.getTarget('distanceSortContainer', 'distance-sort-container');
    if (container) container.classList.remove('d-none');
  }

  /**
   * Hide the distance sort container in the tab header
   */
  hideDistanceSortContainer() {
    const container = this.getTarget('distanceSortContainer', 'distance-sort-container');
    if (container) container.classList.add('d-none');
  }

  /**
   * Toggle distance sorting for the bid list
   * @param {Event} event - Click event from the toggle button
   */
  toggleDistanceSort(event) {
    try {
      event.preventDefault(); event.stopPropagation();
      this.controller.sortByDistanceValue = !this.controller.sortByDistanceValue;
      this.updateDistanceSortButtonState(event.currentTarget, this.controller.sortByDistanceValue);
      this.fetchAndDisplayBidList();
    } catch (error) {
      Logger.error('Failed to toggle distance sort', error);
    }
  }

  /**
   * Update the distance sort button appearance based on state
   * @param {HTMLElement} button - The button element
   * @param {boolean} isActive - Whether distance sorting is active
   */
  updateDistanceSortButtonState(button, isActive) {
    if (!button) button = this.getTarget('distanceSortButton', null) || document.querySelector('[data-action*="toggleDistanceSort"]');
    if (button) {
      button.classList.toggle('btn-outline-info', !isActive);
      button.classList.toggle('btn-info', isActive);
      button.title = isActive ? 'Currently sorting by distance - click to disable' : 'Sort bids by distance from map center';
    }
  }

  /**
   * Activate the Bid List tab
   * This is used when a user clicks CRM buttons or "View Bids" in a marker popup
   */
  activateBidListTab() {
    try {
      // Aggressive tab detection logic
      let bidListTab = null;
      
      // 1. Try Stimulus target first (preferred)
      if (this.controller.hasBidsTabTarget) {
        bidListTab = this.controller.bidsTabTarget;
      }
      
      // 2. Try ID lookup (scoped to controller element first to avoid collisions)
      if (!bidListTab) {
        bidListTab = this.controller.element.querySelector('#bids-tab') || document.getElementById('bids-tab');
      }
      
      // 3. Last resort: CSS selector
      if (!bidListTab) {
        bidListTab = document.querySelector('[data-map-leaflet-target="bidsTab"]');
      }
      
      if (bidListTab) {
        Logger.info('Found Bid List tab for activation', { 
          id: bidListTab.id,
          method: bidListTab.hasAttribute('data-map-leaflet-target') ? 'target' : 'id'
        });

        // Skip if already active
        if (bidListTab.classList.contains('active')) {
          Logger.debug('Bid List tab is already active');
          return;
        }

        // Use a small timeout to ensure the tab switch happens after any Turbo frame loads
        // that might have been triggered by the same click
        setTimeout(() => {
          // Method 1: Bootstrap 5 JavaScript API
          if (typeof bootstrap !== 'undefined' && bootstrap.Tab) {
            try {
              const tab = bootstrap.Tab.getOrCreateInstance(bidListTab) || new bootstrap.Tab(bidListTab);
              tab.show();
              Logger.debug('Tab activated via Bootstrap API');
            } catch (bsError) {
              Logger.warn('Bootstrap Tab API failed, falling back to click', bsError);
              bidListTab.click();
            }
          } else {
            // Method 2: Manual click fallback
            bidListTab.click();
            Logger.debug('Tab activated via manual click');
          }

          // Method 3: Ensure the pane is actually visible (sometimes Bootstrap API doesn't trigger correctly in frames)
          const paneId = bidListTab.getAttribute('data-bs-target') || bidListTab.getAttribute('href');
          if (paneId) {
            const pane = document.querySelector(paneId);
            if (pane && !pane.classList.contains('active')) {
              Logger.warn('Tab pane still not active, forcing visibility');
              // Find all siblings and remove active/show
              if (pane.parentElement) {
                pane.parentElement.querySelectorAll('.tab-pane').forEach(p => {
                  p.classList.remove('active', 'show');
                });
              }
              pane.classList.add('active', 'show');
              
              // Also update the nav-link state
              if (bidListTab.parentElement && bidListTab.parentElement.parentElement) {
                bidListTab.parentElement.parentElement.querySelectorAll('.nav-link').forEach(l => {
                  l.classList.remove('active');
                  l.setAttribute('aria-selected', 'false');
                });
              }
              bidListTab.classList.add('active');
              bidListTab.setAttribute('aria-selected', 'true');
            }
          }
        }, 50);

      } else {
        Logger.warn('Bid List tab button not found in the DOM');
      }
    } catch (error) {
      Logger.error('Failed to activate Bid List tab', error);
    }
  }
}